import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../../stores/authStore';
import { useSiteStore } from '../../stores/siteStore';
import { useSocketStore } from '../../stores/socketStore';
import { useNotificationStore } from '../../stores/notificationStore';
import pushNotificationService from '../../services/pushNotificationService';
import {
  HomeIcon,
  BellAlertIcon,
  VideoCameraIcon,
  MapIcon,
  ExclamationTriangleIcon,
  DocumentChartBarIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  ChartBarIcon,
  BellIcon,
  MagnifyingGlassIcon,
  BuildingOffice2Icon,
  UserGroupIcon,
  CpuChipIcon,
  BoltIcon,
  DeviceTabletIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
  ClipboardDocumentListIcon,
  ShieldExclamationIcon,
  UserCircleIcon,
  BuildingOfficeIcon,
  ScaleIcon,
  SunIcon,
  MoonIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import api, { notificationsAPI } from '../../utils/api';
import OfflineBanner from './OfflineBanner';
import MailQuotaBanner from './MailQuotaBanner';
import NotificationBell from './NotificationBell';
import { useTheme } from '../ThemeSelector';
import GlobalSearch from './GlobalSearch';
import SiteClock from '../SiteClock';

const navigationGroups = [
  {
    label: 'Monitor',
    items: [
      { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
      { name: 'Alerts', href: '/alerts', icon: BellAlertIcon },
      { name: 'Cameras', href: '/cameras', icon: VideoCameraIcon },
      { name: 'Devices', href: '/devices', icon: CpuChipIcon },
      { name: 'Incidents', href: '/incidents', icon: ExclamationTriangleIcon },
    ],
  },
  {
    label: 'Configure',
    items: [
      { name: 'Sites', href: '/sites', icon: BuildingOffice2Icon },
      { name: 'Zones', href: '/zones', icon: MapIcon },
      { name: 'Workers', href: '/workers', icon: UserGroupIcon },
      { name: 'Contractors', href: '/contractors', icon: BuildingOfficeIcon },
    ],
  },
  {
    label: 'Analyze',
    items: [
      { name: 'Analytics', href: '/analytics', icon: ChartBarIcon },
      { name: 'Risk Analytics', href: '/risk-analytics', icon: ShieldExclamationIcon },
      { name: 'Worker Analytics', href: '/worker-analytics', icon: UserCircleIcon },
      { name: 'Contractor Analytics', href: '/contractor-analytics', icon: BuildingOfficeIcon },
      { name: 'Benchmarking', href: '/benchmarking', icon: ScaleIcon },
      { name: 'Reports', href: '/reports', icon: DocumentChartBarIcon },
    ],
  },
  {
    label: 'Admin',
    items: [
      { name: 'Notifications', href: '/notifications', icon: BellIcon },
      { name: 'Users', href: '/users', icon: UserGroupIcon, restrictedTo: ['org_admin', 'super_admin'] },
      { name: 'Audit Log', href: '/audit-log', icon: ClipboardDocumentListIcon, restrictedTo: ['org_admin', 'super_admin'] },
      { name: 'Settings', href: '/settings', icon: Cog6ToothIcon },
    ],
  },
];

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuthStore();
  const { sites, selectedSite, setSelectedSite, fetchSites } = useSiteStore();
  const { activeAlerts, deviceHealth, latestAlert, connect, disconnect, soundAlerts, toggleSoundAlerts, setActiveAlerts } = useSocketStore();
  const { addNotification, setPushEnabled } = useNotificationStore();
  const { darkMode, setDarkMode } = useTheme();
  const navigate = useNavigate();

  // Fetch real active alert count from DB so the badge is correct on load and after page navigation
  const { data: alertCountData } = useQuery({
    queryKey: ['active-alert-count', selectedSite?.id],
    queryFn: () => api.get('/alerts', { params: { status: 'ACTIVE,ESCALATED', limit: 1, ...(selectedSite?.id && { siteId: selectedSite.id }) } }),
    refetchInterval: 60000,
    staleTime: 30000,
  });

  useEffect(() => {
    const total = alertCountData?.data?.pagination?.total;
    if (total !== undefined) {
      setActiveAlerts(total);
    }
  }, [alertCountData, setActiveAlerts]);

  // Safety-net poll for mail quota usage — the socket push only fires once per
  // site per day, so this catches sites that crossed 75% while disconnected.
  const isMailQuotaAdmin = ['org_admin', 'super_admin'].includes(user?.role);
  const { data: mailQuotaUsageData } = useQuery({
    queryKey: ['mail-alert-usage'],
    queryFn: () => notificationsAPI.getMailAlertUsage(),
    enabled: isMailQuotaAdmin,
    refetchInterval: 60000,
    staleTime: 30000,
  });

  useEffect(() => {
    if (mailQuotaUsageData?.data) {
      useSocketStore.getState().mergeMailQuotaWarningsFromUsage(mailQuotaUsageData.data);
    }
  }, [mailQuotaUsageData]);

  // Load the user's "Real-time mobile & browser alerts" preference (Settings >
  // Notifications) so the alert:new socket handler can honor it before firing
  // a browser push — that handler lives in a Zustand store outside React, so
  // the value has to be pushed into shared state rather than read via a hook.
  const { data: notificationSettingsData } = useQuery({
    queryKey: ['notification-settings'],
    queryFn: () => notificationsAPI.getSettings(),
    staleTime: 60000,
  });

  useEffect(() => {
    if (notificationSettingsData?.data) {
      useNotificationStore.getState().setPushPreferenceEnabled(
        notificationSettingsData.data.pushEnabled !== false
      );
    }
  }, [notificationSettingsData]);

  // Initialize push notifications on mount
  useEffect(() => {
    const initPushNotifications = async () => {
      try {
        const enabled = await pushNotificationService.initialize();
        setPushEnabled(enabled);

        if (enabled) {
          console.log('[LAYOUT] Push notifications enabled');

          // Listen for notification events
          pushNotificationService.onNotification((notification) => {
            console.log('[LAYOUT] Notification received:', notification);
            if (notification.data?.alertId) {
              addNotification({
                id: notification.data.alertId,
                type: notification.data.type || 'ALERT',
                severity: notification.data.severity,
                siteId: notification.data.siteId,
                title: notification.title,
                body: notification.body,
                timestamp: new Date(),
                read: false,
              });
            }
          });
        }
      } catch (error) {
        console.warn('[LAYOUT] Failed to initialize push notifications:', error);
      }
    };

    initPushNotifications();
  }, [addNotification, setPushEnabled]);

  useEffect(() => {
    fetchSites();
    connect();
    return () => disconnect();
  }, []);

  useEffect(() => {
    if (selectedSite?.id) {
      useSocketStore.getState().subscribeSite(selectedSite.id);
    }
  }, [selectedSite?.id]);

  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen relative bg-slate-50 dark:bg-slate-950">
      {/* Screen reader live region for alert announcements */}
      <div className="sr-only" aria-live="assertive" aria-atomic="true" role="alert">
        {latestAlert && `New ${latestAlert.severity} alert: ${latestAlert.type?.replace(/_/g, ' ')}`}
      </div>
      <OfflineBanner />
      <MailQuotaBanner />
      <div className="fixed inset-0 -z-10 bg-slate-50 dark:bg-slate-950" />

      {/* Mobile sidebar backdrop */}
      <div className={clsx(
        'fixed inset-0 z-50 lg:hidden transition-opacity duration-300',
        sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
      )}>
        <div
          className="fixed inset-0 bg-slate-900/30 backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
        <div className={clsx(
          'fixed inset-y-0 left-0 w-72 transform transition-transform duration-300 ease-out',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}>
          <SidebarContent onClose={() => setSidebarOpen(false)} activeAlerts={activeAlerts} />
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-40 lg:flex lg:w-72">
        <SidebarContent activeAlerts={activeAlerts} />
      </div>

      {/* Main content */}
      <div className="lg:pl-72">
        {/* Enhanced Top bar */}
        <header className="sticky top-0 z-40 mx-2 sm:mx-4 lg:mx-0 lg:mr-4 mt-2 sm:mt-3">
          <div className="header-bar">
            <div className="flex items-center justify-between gap-2 min-w-0">
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                {/* Mobile menu button */}
                <button
                  className="lg:hidden icon-btn flex-shrink-0"
                  onClick={() => setSidebarOpen(true)}
                  aria-label="Open navigation menu"
                  aria-expanded={sidebarOpen}
                >
                  <Bars3Icon className="w-6 h-6" />
                </button>

                {/* Organization name */}
                {user?.organization?.name && (
                  <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[var(--theme-50)] border border-[var(--theme-200)]">
                    <BuildingOffice2Icon className="w-4 h-4 text-[var(--theme-600)] flex-shrink-0" />
                    <span className="text-sm font-semibold text-[var(--theme-700)] max-w-[120px] truncate">
                      {user.organization.name}
                    </span>
                  </div>
                )}

                {/* Site selector */}
                <div className="relative group min-w-0">
                  <select
                    value={selectedSite?.id || ''}
                    onChange={(e) => setSelectedSite(sites.find(s => s.id === e.target.value))}
                    className="input-select w-28 sm:w-36 md:min-w-[160px]"
                    aria-label="Select site"
                  >
                    <option value="">All Sites</option>
                    {sites.map(site => (
                      <option key={site.id} value={site.id}>{site.name}</option>
                    ))}
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400 group-hover:text-slate-600 transition-colors">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>

                {/* Live clock for the selected site */}
                <div className="hidden md:flex">
                  <SiteClock />
                </div>
              </div>

              <div className="flex items-center gap-1 sm:gap-2 lg:gap-3 flex-shrink-0">
                {/* Edge Device Monitoring */}
                {deviceHealth && Object.values(deviceHealth).length > 0 && (
                  <div className="hidden xl:flex items-center gap-3 pr-3 border-r border-slate-200/80">
                    {Object.values(deviceHealth).map((device) => (
                      <div key={device.deviceId} className="flex flex-col items-end">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider leading-none mb-1">
                          {device.deviceName}
                        </span>
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1" title="CPU Usage" aria-label={`CPU Usage: ${Math.round(device.cpuUsage ?? 0)}%`} role="status">
                            <CpuChipIcon className="w-3 h-3 text-slate-400" aria-hidden="true" />
                            <span className={clsx("text-[11px] font-bold", (device.cpuUsage ?? 0) > 80 ? "text-red-500" : "text-slate-600")}>
                              {Math.round(device.cpuUsage ?? 0)}%
                            </span>
                          </div>
                          <div className="flex items-center gap-1" title="GPU Usage" aria-label={`GPU Usage: ${Math.round(device.gpuUsage ?? 0)}%`} role="status">
                            <BoltIcon className="w-3 h-3 text-amber-500" aria-hidden="true" />
                            <span className={clsx("text-[11px] font-bold", (device.gpuUsage ?? 0) > 80 ? "text-red-500" : "text-slate-600")}>
                              {Math.round(device.gpuUsage ?? 0)}%
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Global Search */}
                <div className="hidden lg:block">
                  <GlobalSearch />
                </div>

                {/* Dark Mode Toggle */}
                <button
                  onClick={() => setDarkMode(darkMode === 'dark' ? 'light' : 'dark')}
                  className="icon-btn"
                  title={darkMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                  aria-label={darkMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {darkMode === 'dark' ? (
                    <SunIcon className="w-5 h-5" />
                  ) : (
                    <MoonIcon className="w-5 h-5" />
                  )}
                </button>

                {/* Sound Alert Toggle */}
                <button
                  onClick={toggleSoundAlerts}
                  className="icon-btn"
                  title={soundAlerts ? 'Mute alert sounds' : 'Enable alert sounds'}
                  aria-label={soundAlerts ? 'Mute alert sounds' : 'Enable alert sounds'}
                >
                  {soundAlerts ? (
                    <SpeakerWaveIcon className="w-5 h-5" />
                  ) : (
                    <SpeakerXMarkIcon className="w-5 h-5 text-slate-400" />
                  )}
                </button>

                {/* Notification Bell */}
                <NotificationBell />

                {/* Active alerts indicator */}
                <div aria-live="polite" aria-atomic="true" className="hidden sm:block">
                  {activeAlerts > 0 && (
                    <div className="alert-indicator" role="status">
                      <span className="relative flex h-2.5 w-2.5" aria-hidden="true">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
                      </span>
                      <span className="text-sm font-bold text-red-600">
                        {activeAlerts}
                      </span>
                    </div>
                  )}
                </div>

                {/* User menu */}
                <div className="flex items-center gap-1 sm:gap-2 pl-2 sm:pl-3 border-l border-slate-200/80">
                  <div className="hidden sm:flex flex-col items-end">
                    <span className="text-sm font-semibold text-slate-800 max-w-[100px] truncate">
                      {user?.firstName} {user?.lastName}
                    </span>
                    <span className="text-xs text-slate-500 capitalize">{user?.role}</span>
                  </div>
                  <div className="user-avatar">
                    <span className="text-sm font-bold">
                      {user?.firstName?.[0]}{user?.lastName?.[0]}
                    </span>
                  </div>
                  <button
                    onClick={() => setShowLogoutConfirm(true)}
                    className="icon-btn icon-btn-danger"
                    title="Logout"
                  >
                    <ArrowRightOnRectangleIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-2 sm:p-4 lg:pr-4 lg:pl-0">
          <div className="animate-fade-in">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="logout-dialog-title">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm mx-4 animate-fade-in">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <ArrowRightOnRectangleIcon className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h2 id="logout-dialog-title" className="text-base font-semibold text-slate-800">Sign out?</h2>
                <p className="text-sm text-slate-500">You will be returned to the login screen.</p>
              </div>
            </div>
            <div className="flex gap-3 justify-end mt-6">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleLogout}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SidebarContent({ onClose, activeAlerts = 0 }) {
  const { user } = useAuthStore();

  const { data: health } = useQuery({
    queryKey: ['system-health'],
    queryFn: () => api.get('/health/detailed').then(res => res.data),
    refetchInterval: 60000,   // 60 s — reduces flicker from short transient disconnects
    retry: 1,
    staleTime: 55000,
  });

  const healthStatus = health?.status || 'unknown';
  const checks = health?.checks || {};
  const aiDetails = health?.details?.aiEngine || {};
  // "System Online" when DB + Redis are up, regardless of AI Engine state
  const coreHealthy = checks.database && checks.redis;
  const allHealthy = healthStatus === 'healthy';
  const isDegraded = healthStatus === 'degraded';   // core services down
  const isAiDegraded = healthStatus === 'ai_degraded'; // only AI offline

  return (
    <div className="sidebar-container m-4 lg:m-4">
      <div className="sidebar-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-dark.png" alt="KenSafety" className="h-20 object-contain brightness-0 invert" />
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="lg:hidden p-2 rounded-xl text-white/70 hover:text-white hover:bg-white/10 transition-all"
              aria-label="Close navigation menu"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto" aria-label="Main navigation">
        {navigationGroups.map((group) => {
          const items = group.items.filter(item => !item.restrictedTo || item.restrictedTo.includes(user?.role));
          if (items.length === 0) return null;

          return (
            <div key={group.label} className="sidebar-nav-section">
              <div className="sidebar-nav-section-label">{group.label}</div>
              {items.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.href}
                  className={({ isActive }) => clsx(
                    'sidebar-nav-item',
                    isActive && 'active'
                  )}
                  onClick={onClose}
                >
                  <div className="sidebar-nav-icon">
                    <item.icon className="w-5 h-5" />
                  </div>
                  <span className="flex-1 font-medium">{item.name}</span>
                  {item.name === 'Alerts' && activeAlerts > 0 && (
                    <span className="sidebar-alert-badge">
                      {activeAlerts}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          );
        })}
      </nav>

      {/* Enhanced Footer */}
      <div className="p-4">
        <div className="sidebar-status-card">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className={clsx(
                'w-10 h-10 rounded-xl flex items-center justify-center shadow-lg bg-gradient-to-br',
                isDegraded ? 'from-red-400 to-red-600' : isAiDegraded ? 'from-amber-400 to-amber-600' : coreHealthy ? 'from-emerald-400 to-emerald-600' : 'from-slate-400 to-slate-600'
              )}>
                <CpuChipIcon className="w-5 h-5 text-white" />
              </div>
              <div className={clsx(
                'absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white',
                isDegraded ? 'bg-red-400 animate-pulse' : isAiDegraded ? 'bg-amber-400 animate-pulse' : coreHealthy ? 'bg-emerald-400 animate-pulse' : 'bg-slate-400'
              )} />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-slate-800">
                {isDegraded ? 'System Degraded' : coreHealthy ? 'System Online' : 'Checking...'}
              </p>
              <p className="text-xs text-slate-500">
                {isDegraded ? 'Core services impacted' : isAiDegraded ? 'AI Engine offline' : coreHealthy ? 'All services running' : 'Connecting to services'}
              </p>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-slate-200/80 space-y-1.5">
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-500">Database</span>
              <span className={clsx('font-semibold', checks.database ? 'text-emerald-600' : checks.database === false ? 'text-red-500' : 'text-slate-400')}>
                {checks.database ? 'Connected' : checks.database === false ? 'Down' : '—'}
              </span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-500">Redis</span>
              <span className={clsx('font-semibold', checks.redis ? 'text-emerald-600' : checks.redis === false ? 'text-red-500' : 'text-slate-400')}>
                {checks.redis ? 'Connected' : checks.redis === false ? 'Down' : '—'}
              </span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-500">AI Engine</span>
              <span className={clsx('font-semibold', checks.aiEngine ? 'text-emerald-600' : checks.aiEngine === false ? 'text-amber-500' : 'text-slate-400')}>
                {checks.aiEngine
                  ? `Online${aiDetails.onlineCount > 1 ? ` (${aiDetails.onlineCount})` : ''}`
                  : checks.aiEngine === false
                    ? `Offline${aiDetails.totalCount > 0 ? ` (0/${aiDetails.totalCount})` : ''}`
                    : '—'}
              </span>
            </div>
            <div className="mt-2">
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className={clsx(
                  'h-full rounded-full transition-all duration-500 bg-gradient-to-r',
                  isDegraded ? 'w-1/3 from-red-400 to-red-500' : isAiDegraded ? 'w-2/3 from-amber-400 to-amber-500' : coreHealthy ? 'w-full from-emerald-400 to-emerald-500' : 'w-1/4 from-slate-300 to-slate-400'
                )} />
              </div>
            </div>
          </div>
        </div>
        <p className="text-center text-[10px] text-slate-400 mt-3">
          Powered by <span className="font-semibold text-slate-500">Intelense</span>
        </p>
      </div>

    </div>
  );
}
